/**
 * ShareButton Component
 *
 * Reusable button for copying entity deep links to clipboard.
 * Shows success/error toasts and prevents event propagation.
 *
 * Used in:
 * - Card modal (sticky header)
 * - Comments (hover-reveal)
 * - Board header
 */

import React from 'react';
import { Icon, IconName } from '@/icons';
import { DeepLinkEntity } from '@/utils/deep-linking';
import { copyEntityLink } from '@/utils/clipboard';
import { useToast } from '@/hooks/useToast';
import { useThemeColors } from '@/styles';
import { logger } from '@/utils/logger';

/**
 * ShareButton Props
 */
export interface ShareButtonProps {
  /** Entity type (card, comment, board) */
  entity: DeepLinkEntity;

  /** Entity ID */
  id: string;

  /** Optional aria-label for accessibility */
  ariaLabel?: string;

  /** Optional CSS classes */
  className?: string;

  /** Optional inline styles */
  style?: React.CSSProperties;

  /** Override icon color (for theme-isolated containers) */
  iconColor?: string;

  /** Optional query params to append to the generated link */
  queryParams?: Record<string, string>;

  /** Optional callback after successful copy */
  onClick?: () => void;
}

/**
 * ShareButton Component
 *
 * Self-contained button that copies entity deep links to clipboard.
 * Handles toast notifications and event propagation automatically.
 * Prevents duplicate clicks with temporary disabled state.
 *
 * @example
 * ```tsx
 * <ShareButton
 *   entity={DeepLinkEntity.CARD}
 *   id="card-123"
 *   ariaLabel="Copy card link"
 * />
 * ```
 */
export function ShareButton({
  entity,
  id,
  ariaLabel,
  className = '',
  style,
  iconColor,
  queryParams,
  onClick,
}: ShareButtonProps): React.ReactElement {
  const toast = useToast();
  const colors = useThemeColors();
  const [isDisabled, setIsDisabled] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);
  const [isPressed, setIsPressed] = React.useState(false);

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    // Prevent event bubbling to parent elements
    e.stopPropagation();

    // Prevent duplicate clicks
    if (isDisabled) {
      return;
    }

    // Disable button temporarily
    setIsDisabled(true);

    try {
      const success = await copyEntityLink(entity, id, queryParams);

      if (success) {
        toast.success('Link copied!', {
          message: 'Link has been copied to your clipboard',
          duration: 2000,
        });

        // Call optional callback
        onClick?.();
      } else {
        toast.error('Failed to copy link', {
          message: 'Please try again or copy from the URL bar',
          duration: 3000,
        });
      }
    } catch (error) {
      logger.error('[ShareButton] Error copying link:', error);
      toast.error('Failed to copy link', {
        message: 'Please try again or copy from the URL bar',
        duration: 3000,
      });
    } finally {
      // Re-enable button after 500ms to prevent accidental double-clicks
      setTimeout(() => {
        setIsDisabled(false);
      }, 500);
    }
  };

  const defaultAriaLabel = `Copy ${entity} link`;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isDisabled}
      aria-label={ariaLabel || defaultAriaLabel}
      className={`
        inline-flex
        items-center
        justify-center
        h-11
        w-11
        rounded-md
        transition-all
        duration-200
        flex-shrink-0
        ${isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
        ${className}
      `}
      style={{
        backgroundColor: isDisabled
          ? 'transparent'
          : isPressed
            ? colors.active.background
            : isHovered
              ? colors.hover.background
              : 'transparent',
        color: isDisabled
          ? colors.text.secondary
          : isPressed
            ? colors.active.text
            : isHovered
              ? colors.hover.text
              : colors.text.secondary,
        ...style,
      }}
      onMouseEnter={() => !isDisabled && setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setIsPressed(false);
      }}
      onMouseDown={() => !isDisabled && setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
    >
      <Icon name={IconName.LINK} size="md" color={iconColor} />
    </button>
  );
}
